import { UpdateUserDto } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, ViewChild, computed, inject, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService, QiraUser } from '@app/services/auth.service';
import { PrimeNgMessageService } from '@app/services/primeNgMessage.service';
import { ModalService } from '@app/components/modal/modal.service';
import { LoadingDotsComponent } from '@app/chat/components/loading-dots/loading-dots.component';
import { ProfileDataFormComponent } from './profile-data-form.component';
import { TranslocoPipe } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';
import { Button } from 'primeng/button';

@Component({
  selector: 'app-profile-data',
  templateUrl: './profile-data.component.html',
  imports: [CommonModule, TranslocoPipe, LoadingDotsComponent, ProfileDataFormComponent, Button],
  standalone: true,
})
export class ProfileDataComponent {
  user = input<QiraUser | undefined>(undefined);
  loading = input<boolean>(false);
  onChange = output<QiraUser | undefined>();

  @ViewChild('profileForm', { static: false }) formComponent: ProfileDataFormComponent | undefined;

  private readonly route = inject(ActivatedRoute);
  authService = inject(AuthService);
  modalService = inject(ModalService);
  messageService = inject(PrimeNgMessageService);

  isAdmin = computed(() => this.authService.userIsAdmin());

  isEditing = signal<boolean>(false);

  serviceUrl = "https://www.qira.net/contact"

  salutationKey = computed(() => {
    switch (this.user()?.profile?.salutation) {
      case 'na':
        return null;
      case 'mr':
        return 'profile.salutation.mister';
      case 'mrs':
        return 'profile.salutation.mistress';
      default:
        return null;
    }
  });

  toggleEditForm() {
    if (this.isEditing()) {
      this.formComponent?.reset();
      this.isEditing.set(false);
    } else {
      this.isEditing.set(true);
    }
  }

  async onFormSave(updatedUser: Partial<UpdateUserDto>) {
    try {
      await this.authService.updateUser(updatedUser);
      this.messageService.showSuccess('profile.edit.success');
      // reset form values
      this.formComponent?.reset();
      this.toggleEditForm();
      this.onChange.emit(this.authService.currentUser());
    } catch (err) {
      this.messageService.showError('profile.edit.error');
      this.toggleEditForm();

      console.error('[ProfileDataComponent] Failed to update user profile', err);
      Sentry.captureException(err, {
        tags: {
          location: 'ProfileDataComponent',
          feature: 'UserProfile',
          message: 'Failed to update user profile',
        },
      });
    }
  }

  async onDeleteAccountClicked() {
    const confirmed = await this.modalService.confirm(
      'profile.deleteAccount.title',
      'profile.deleteAccount.confirmText',
      'profile.deleteAccount.confirmButton',
      'global.noThanks'
    );

    if (confirmed) {
      window.open(this.serviceUrl, '_blank');
    }
  }
}
